import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const NewCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { sportId, categoryId, categoryName: initialCategoryName } = location.state || {};
  const token = localStorage.getItem('auth-token');

  const [categoryName, setCategoryName] = useState(initialCategoryName || '');
  const baselineRef = useRef(null);

  const AUTH_HEADER = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-authorization': process.env.REACT_APP_AUTH_TOKEN, 
  };

  const buildSubmitPayload = (name = categoryName) => ({
    id: categoryId || null,
    sportId: sportId ? Number(sportId) : null,
    name: (name || '').trim(),
  });

  useEffect(() => {
    baselineRef.current = buildSubmitPayload(initialCategoryName || '');
  }, [categoryId, sportId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildSubmitPayload();

    if (categoryId) {
      const { digest, html } = buildDiffPreview(baselineRef.current || {}, payload);
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          timer: 2500,
          showConfirmButton: false,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    } else {
      const { html } = buildDiffPreview({}, payload);
      const confirm = await Swal.fire({
        title: 'Create this category?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Create',
      });
      if (!confirm.isConfirmed) return;
    }

    const apiUrl = `${process.env.REACT_APP_API_CATEGORY_CREATE}?sportId=${sportId}&name=${categoryName}&categoryId=${categoryId || ''}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: AUTH_HEADER,
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      fireAudit({
        entity: 'category',
        affected_id: categoryId || null,
        action: categoryId ? 'update' : 'create',
        section: 'SPORT',
        subSection: 'Category',
        businessApi: apiUrl,
        businessMethod: 'POST',
        oldPayload: baselineRef.current || {},
        newPayload: payload,
        extra: { page: 'NewCategory' },
      });

      if (response.ok && data.success !== false) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: categoryId ? 'Category updated successfully!' : 'Category created successfully!',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
        baselineRef.current = payload;
        setCategoryName('');
        navigate(-1);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to create/update category',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred. Please try again.',
        toast: true,
        position: 'top-end',
        timer: 3000,
        showConfirmButton: false,
      });
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/sportList" className="breadcrumb-link">
          Sport List
        </Link>{' '}
        &gt; {categoryId ? 'Edit Category' : 'New Category'}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            id="name-input"
            type="text"
            name="categoryName"
            className={`walkin-form ${categoryName ? 'has-value' : ''}`}
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            placeholder="Enter Category Name"
            required
          />
          {categoryName && <label htmlFor="name-input">Category Name</label>}
        </div>

        <button className="submit-button" type="submit">
          {categoryId ? 'Update' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default NewCategory;
